// All pages / capabilities that can be permission-controlled.
//
// Three shapes of permission rows live in the same table:
//
//   - `page` (default) — "can the user see this page / tab?". Carries
//     can_read + can_create + can_edit; consumers gate the page entry
//     on can_read and the Add/Edit affordances on can_create/can_edit.
//
//   - `stream` — "can the user write to events of this event_stream?".
//     can_read is implicit (events of all streams are always visible
//     in lists). can_create gates creating a NEW event in this stream.
//     can_edit gates editing existing events of this stream, any of
//     their sub-resources (session, reservation, space booking, product,
//     package, catering, equipment, hotel block), AND the cancel-event
//     button (which POSTs to /api/events/:id/cancel). The two are
//     independent — a role can be allowed to edit existing seminars
//     without being allowed to create new ones. The matrix UI hides
//     the read column for these rows since read is implicit.
//
//   - `read-only` — a view-only sub-capability. Carries can_read; the matrix
//     deliberately does not offer create/edit because the surface has no
//     write actions of its own.
//
// pageKey for `page` shape MUST match the route name in router/index.ts
// (or the tab id for sub-tab keys like admin-* / ps-tab-*). pageKey
// for `stream` shape is always `stream:<event_stream>`.

export interface PageDefinition {
  pageKey: string
  label: string
  group: string
  /**
   * `true` for capabilities that are NOT navigable pages — per-stream write
   * gates, function-sheet workflow toggles, the customer-portal affordance,
   * Maya, raw-table read gates, etc. The permissions matrix renders these in a
   * separate "Special Permissions" section, apart from the page rows (which are
   * ordered to mirror the left menu).
   */
  special?: boolean
  /**
   * Default `page` — full read/create/edit triple.
   * `stream` — read is implicit; can_create gates new-event creation
   * in this event_stream, can_edit gates writes (including cancel)
   * against existing events of this stream.
   * `read-only` — only can_read is meaningful.
   */
  permissionShape?: 'page' | 'stream' | 'read-only'
  /**
   * What read / create / edit actually mean for this row, when that is not
   * "see the page / add a thing / change a thing".
   *
   * Shown under the label in the permissions matrix. Maya is why this exists:
   * its three columns govern what the ASSISTANT may do with the user's data,
   * which no one could infer from a row labelled "Maya AI Assistant" with the
   * same three checkboxes as every page.
   */
  description?: string
}

/**
 * Per-stream rights for the PARTS of an event (V219). `stream:<s>` stays the
 * right for the event itself; these split what its Edit used to cover:
 *   blocks        PMS / hotel blocks
 *   participants  participants — Delete = storno / no-show
 *   spaces        space bookings — Delete = cancel the booking
 *   products      F&B / equipment (UI-enforced only for now)
 * Checked through useStreamAccess().canStreamArea(stream, area, action).
 */
export const STREAM_AREAS = ['blocks', 'participants', 'spaces', 'products'] as const
export type StreamArea = (typeof STREAM_AREAS)[number]
const STREAM_LABELS: Record<string, string> = { mice: 'MICE', internal: 'Internal', seminars: 'Seminars', bs: 'BS' }
const AREA_TEXT: Record<StreamArea, { label: string; description: string }> = {
  blocks: { label: 'Hotelblöcke', description: 'Create = request a hotel block · Edit = resize / re-date / change category · Delete = release a block.' },
  participants: { label: 'Teilnehmer', description: 'Create = add participants · Edit = change participants · Delete = storno / no-show.' },
  spaces: { label: 'Raumbuchungen', description: 'Create = book a space · Edit = change a booking, set the main room · Delete = cancel a booking.' },
  products: { label: 'F&B / Equipment', description: 'Create = add products / packages · Edit = quantity, price, charge · Delete = remove / cancel a line. (Enforced in the UI only.)' },
}
const STREAM_AREA_PAGES: PageDefinition[] = ['mice', 'internal', 'seminars', 'bs'].flatMap(stream =>
  STREAM_AREAS.map(area => ({
    pageKey: `stream:${stream}:${area}`,
    label: `Stream ▸ ${STREAM_LABELS[stream]} ▸ ${AREA_TEXT[area].label}`,
    group: 'Event Streams',
    special: true,
    permissionShape: 'stream' as const,
    description: AREA_TEXT[area].description,
  })),
)

/**
 * Per-stream "gültig" sign-off capabilities (V233) — `events:set-valid:<stream>`.
 *
 * `read-only` shape on purpose: signing off is ONE ability, so the matrix shows
 * one checkbox per stream. A `stream:<s>:<area>` row would render the
 * Create/Edit/Delete triple and leave two boxes doing nothing.
 *
 * Backend counterpart: `requireSetValidRight` (EventHub-Backend
 * `src/middleware/requireStreamRight.ts`), which gates all four
 * /api/events/:id/valid routes.
 */
const SET_VALID_PAGES: PageDefinition[] = ['mice', 'internal', 'seminars', 'bs'].map(stream => ({
  pageKey: `events:set-valid:${stream}`,
  label: `Events ▸ Mark as Valid (gültig) ▸ ${STREAM_LABELS[stream]}`,
  group: 'Capabilities',
  special: true,
  permissionShape: 'read-only' as const,
  description: `Read = may mark a ${STREAM_LABELS[stream]} event (and its sessions) as "gültig" and clear it again.`,
}))

export const allPages: PageDefinition[] = [
  // ─── Core ──────────────────────────────────────────────────────
  // Dashboard is the new role-aware landing page. When the user's role
  // holds can_read=true on `dashboard`, it becomes the default route
  // after login (router root sends them to /dashboard) and the entry
  // surfaces at the top of the left menu. Roles without it keep the
  // legacy events.vue landing — same behaviour as before the rollout.
  { pageKey: 'dashboard', label: 'Dashboard', group: 'Core' },
  { pageKey: 'events', label: 'Events', group: 'Core' },
  { pageKey: 'event-management', label: 'Event Management', group: 'Core' },
  { pageKey: 'business-school', label: 'Business School', group: 'Core' },
  { pageKey: 'availabilities', label: 'Availabilities', group: 'Core' },
  // Events × room-category block overview for a period (read-only in phase 1).
  // Capability (not a route): the Block Manager TAB of Hotel Optimisation. It
  // keeps its own key so the Core audience that had the page keeps the tab —
  // the router grants entry to /hotel-optimisation on either key.
  { pageKey: 'block-manager', label: 'Block Manager', group: 'Core' },
  { pageKey: 'reservations', label: 'Reservations', group: 'Core' },
  // Capability (not a route): the block overlay on the Reservations ▸ Zimmer tab.
  // can_read shows the block-management toggle + the blocks (view only);
  // can_edit gates the "move to another category" action in the block modal.
  // Colon-namespaced so it never becomes a landing route. Off by default.
  { pageKey: 'reservations:block-management', label: 'Block Management (Reservations)', group: 'Capabilities', special: true },
  // Capability (not a route): who may make the hotel-side decision on a
  // requested room block — approve/reject a release and set event protection.
  // Replaces the former `hotel-release-approver` role; grant it to whichever
  // role should hold the approval workflow. can_read is the whole gate.
  // Admin roles bypass as everywhere else. Off by default.
  { pageKey: 'hotel-release-approve', label: 'Hotel Block Release Approval', group: 'Capabilities', special: true },
  // Reception board: arrivals / in-house / departures. can_read shows the
  // board; can_edit gates the check-in action, so a role can watch the desk
  // without being able to move a guest's state.
  { pageKey: 'pms-board', label: 'Reception', group: 'Core' },
  { pageKey: 'room-occupancy', label: 'Space Center', group: 'Core' },
  // Read-only preview of what each room's digital sign shows at a chosen time.
  { pageKey: 'signage-emulator', label: 'Signage Emulator', group: 'Core' },
  { pageKey: 'booking-plan', label: 'Booking Plan', group: 'Core' },
  { pageKey: 'exporter', label: 'Exporter', group: 'Core' },
  { pageKey: 'mews-finance', label: 'MEWS Finance', group: 'Core' },
  // "Inside EventHub" — the in-app learning library. Read = watch the videos
  // (everyone); managing (upload/edit/delete) is the separate capability below.
  { pageKey: 'inside-eventhub', label: 'Inside EventHub (Learning)', group: 'Core' },
  { pageKey: 'inside-eventhub:manage', label: 'Inside EventHub ▸ Manage Videos', group: 'Capabilities', special: true, permissionShape: 'read-only', description: 'Read = may upload, edit and delete learning videos.' },
  // Capability (not a route): gates the "customer portal link" affordance on
  // the event edit modal and the participant/reservation editor. Only roles
  // with can_read here may generate + open a customer portal link. Off by
  // default — grant it explicitly in Administration ▸ Roles & Permissions.
  { pageKey: 'customer-portal', label: 'Customer Portal Links', group: 'Capabilities', special: true },

  // Capability (not a route): the function-sheet review workflow, split into
  // two independent grants. Colon-namespaced so neither becomes a landing
  // route. Both off by default — grant in Administration ▸ Roles & Permissions.
  //   - mark-checked: can_read lets the role mark a function sheet as "geprüft"
  //     (the "Als geprüft markieren" button in the sheet preview). Without it
  //     the button is hidden.
  //   - edit-after-checked: can_read lets the role keep editing an event whose
  //     function sheet has already been marked checked. Without it, editing is
  //     locked once the sheet is checked.
  { pageKey: 'function-sheet:mark-checked', label: 'Function Sheet ▸ Mark as Checked', group: 'Capabilities', special: true, permissionShape: 'read-only', description: 'Read = may mark a function sheet as "geprüft".' },
  // Capability (not a route): V202 event "gültig", made per-stream by V233.
  // can_read lets the role mark an event OF THAT STREAM as valid (and clear it)
  // from the check next to the "gesehen" eye — the event managers' "my work
  // here is done". Everyone sees the state. A session inherits its event's
  // stream, so the same row governs session sign-off.
  //
  // The single global `events:set-valid` these replaced is retired: V233 copies
  // every holder onto all four streams and deletes the global row, so one role
  // can no longer sign off a stream it does not own. Rows are generated below
  // from EVENT_STREAMS — see SET_VALID_PAGES.
  ...SET_VALID_PAGES,
  { pageKey: 'function-sheet:edit-after-checked', label: 'Function Sheet ▸ Edit After Checked', group: 'Capabilities', special: true, permissionShape: 'read-only', description: 'Read = may still edit an event after its function sheet is marked checked.' },

  // Capability (not a route): D167 / EH-0032. Lets a role that CANNOT edit MICE
  // events still push one stage forward from the event overview —
  // qualify → proposal, the handover point where the hotel has released rooms
  // and spaces and the event becomes offerable. Deliberately narrow:
  //   - forward only, and only that single step (see `advanceStatusTarget`);
  //   - nothing else on the event becomes editable;
  //   - MICE editors and admins already have the full ladder via
  //     `canEditStream('mice')` and do not need this key.
  // Colon-namespaced on purpose: `firstAccessiblePage()` skips keys containing
  // ':' so this never becomes somebody's landing route.
  // Off by default — grant it to the hotel role in
  // Administration ▸ Roles & Permissions (the user must re-login to pick it up).
  { pageKey: 'event:status-advance', label: 'Event ▸ Advance Stage (hotel)', group: 'Capabilities', special: true },

  // ─── Event Streams ─────────────────────────────────────────────
  // Per-stream write gate, split into create vs edit. A user without
  // `stream:mice can_create=true` can't create a new MICE event; a
  // user without `stream:mice can_edit=true` can't modify an existing
  // MICE event or its sub-resources (sessions, reservations, space
  // bookings, products, packages, catering, equipment, hotel blocks).
  // Read is implicit for every stream.
  { pageKey: 'stream:mice',     label: 'Stream ▸ MICE',     group: 'Event Streams', special: true, permissionShape: 'stream', description: 'The MICE event itself: Create = start one · Edit = change it, its sessions and its status, cancel it · Delete = delete it. Its parts (blocks, participants, spaces, F&B) have their own rows below. Reading is implicit.' },
  { pageKey: 'stream:internal', label: 'Stream ▸ Internal', group: 'Event Streams', special: true, permissionShape: 'stream', description: 'Create = start a new internal event · Edit = change existing internal events and their sub-resources.' },
  { pageKey: 'stream:seminars', label: 'Stream ▸ Seminars', group: 'Event Streams', special: true, permissionShape: 'stream', description: 'Create = start a new seminar · Edit = change existing seminars and their sub-resources.' },
  { pageKey: 'stream:bs',       label: 'Stream ▸ BS',       group: 'Event Streams', special: true, permissionShape: 'stream', description: 'Create = start a new BS event · Edit = change existing BS events and their sub-resources.' },
  // The event's parts, per stream (V219) — see STREAM_AREA_PAGES above.
  ...STREAM_AREA_PAGES,
  // The "gültig" sign-off, per stream (V233) — see SET_VALID_PAGES above.
  // Grouped under Capabilities with the other colon-namespaced abilities, not
  // here, so the matrix keeps all four sign-off checkboxes side by side.

  // ─── Data ──────────────────────────────────────────────────────
  { pageKey: 'mews-services', label: 'MEWS Data', group: 'Data' },
  { pageKey: 'analytics', label: 'Analytics', group: 'Data' },
  // observability + user-experience moved into the Dev Ops group below
  // — they're now tabs inside /dev-ops alongside Error Log, Azure
  // DevOps, E2E Tester and Reports. Standalone routes still work for
  // direct URLs; the key just groups with its sibling tabs in the
  // permissions matrix.
  { pageKey: 'stock-management', label: 'Stock Management', group: 'Data' },
  { pageKey: 'mice-board', label: 'MICE Board', group: 'Data' },
  { pageKey: 'masterdata-chain', label: 'Master Data Chain', group: 'Data' },
  { pageKey: 'data-management', label: 'Data Management', group: 'Data' },
  { pageKey: 'contacts', label: 'Contacts', group: 'Data' },
  { pageKey: 'fnb-equipment', label: 'F&B / Equipment', group: 'Data' },

  // ─── Administration ────────────────────────────────────────────
  // Each tab is independently gated. The legacy `administration`
  // umbrella key has been removed — granting access now requires
  // explicit toggles per tab. Existing roles that relied on the
  // umbrella will lose access until backfilled (intentional, per the
  // migration call we made).
  { pageKey: 'admin-properties',          label: 'Administration ▸ Properties',          group: 'Administration' },
  { pageKey: 'admin-integrations',        label: 'Administration ▸ Integrations',        group: 'Administration' },
  { pageKey: 'admin-currencies',          label: 'Administration ▸ Currencies',          group: 'Administration' },
  { pageKey: 'admin-property-settings',   label: 'Administration ▸ Property Settings',   group: 'Administration' },
  { pageKey: 'users-management',          label: 'Administration ▸ Users',               group: 'Administration' },
  { pageKey: 'roles-management',          label: 'Administration ▸ Roles & Permissions', group: 'Administration' },
  { pageKey: 'themes',                    label: 'Administration ▸ Themes',              group: 'Administration' },
  // Tabs folded in from legacy standalone routes. Each gets its own
  // admin-* gate so role membership in the Administration matrix is
  // self-contained. The legacy page keys (`exporter`, `raw-tables`)
  // still gate the standalone /exporter and /raw-tables routes.
  { pageKey: 'admin-exporter',            label: 'Administration ▸ Exporter',            group: 'Administration' },
  { pageKey: 'admin-raw-tables',          label: 'Administration ▸ Raw Tables',          group: 'Administration' },

  // ─── Property Setup ────────────────────────────────────────────
  // The legacy `property-setup` umbrella has also been removed —
  // grant each tab explicitly.
  { pageKey: 'ps-tab-spaces',                label: 'Property Setup ▸ Spaces',                group: 'Property Setup' },
  { pageKey: 'ps-tab-rates',                 label: 'Property Setup ▸ Rates',                 group: 'Property Setup' },
  { pageKey: 'ps-tab-storno',                label: 'Property Setup ▸ Storno Rules',          group: 'Property Setup' },
  { pageKey: 'ps-tab-room-types',            label: 'Property Setup ▸ Hotelzimmer',           group: 'Property Setup' },
  { pageKey: 'ps-tab-accounting-categories', label: 'Property Setup ▸ Buchungskategorien',    group: 'Property Setup' },
  { pageKey: 'ps-tab-tax-codes',             label: 'Property Setup ▸ Steuercodes',           group: 'Property Setup' },
  { pageKey: 'ps-tab-products',              label: 'Property Setup ▸ Products',              group: 'Property Setup' },
  { pageKey: 'ps-tab-packages',              label: 'Property Setup ▸ Pakete',                group: 'Property Setup' },
  { pageKey: 'ps-tab-price-lists',           label: 'Property Setup ▸ Preislisten',           group: 'Property Setup' },
  { pageKey: 'ps-tab-rules',                 label: 'Property Setup ▸ Rules',                 group: 'Property Setup' },
  { pageKey: 'ps-tab-webhooks',              label: 'Property Setup ▸ Webhooks',              group: 'Property Setup' },
  { pageKey: 'ps-tab-hotel-optimizer',       label: 'Property Setup ▸ Hotel Optimizer',       group: 'Property Setup' },
  // NOTE: 'ps-tab-contacts' is kept despite the Property Setup ▸ Contacts tab
  // being removed — it also gates the PMS "Gäste & Firmen" (accounts) feature
  // (PmsAccountsPanel, pms-settings). Deleting it would break create/edit there.
  { pageKey: 'ps-tab-contacts',              label: 'PMS ▸ Gäste & Firmen',                  group: 'Property Setup' },
  { pageKey: 'ps-tab-mews',                  label: 'Property Setup ▸ MEWS',                  group: 'Property Setup' },
  { pageKey: 'ps-tab-series',                label: 'Property Setup ▸ Event Series',          group: 'Property Setup' },
  { pageKey: 'ps-tab-tags',                  label: 'Property Setup ▸ Tags',                  group: 'Property Setup' },

  // ─── Ops / Diagnostics ─────────────────────────────────────────
  // The Ops group is reserved for surfaces that *aren't* tabs of
  // /dev-ops. The error-log / e2e-tester keys moved into Dev Ops below
  // along with their sibling tabs (Azure DevOps, Observability, etc.).
  { pageKey: 'middleware', label: 'Middleware', group: 'Ops' },
  { pageKey: 'audit-log', label: 'Audit Log', group: 'Ops' },
  { pageKey: 'conflict-resolution', label: 'Conflict Resolution', group: 'Ops' },
  { pageKey: 'hotel-optimisation', label: 'Hotel Optimisation', group: 'Ops' },
  { pageKey: 'revenue-management', label: 'Revenue Management', group: 'Data' },

  // ─── Dev Ops ───────────────────────────────────────────────────
  // /dev-ops is a six-tab page; each key gates one tab (and the
  // legacy standalone route, if any). Entry to /dev-ops is granted
  // when the role holds ANY of these keys — same strict-per-tab
  // pattern as administration / property-setup. Grouping all six
  // here in the permissions matrix mirrors how they're presented in
  // the UI so role admins toggle them as a coherent set.
  { pageKey: 'error-log',       label: 'Dev Ops ▸ Error Log',       group: 'Dev Ops' },
  { pageKey: 'azure-pbis',      label: 'Dev Ops ▸ Azure DevOps',    group: 'Dev Ops' },
  { pageKey: 'observability',   label: 'Dev Ops ▸ Observability',   group: 'Dev Ops' },
  { pageKey: 'user-experience', label: 'Dev Ops ▸ User Experience', group: 'Dev Ops' },
  { pageKey: 'e2e-tester',      label: 'Dev Ops ▸ E2E Tester',      group: 'Dev Ops' },
  { pageKey: 'reports',         label: 'Dev Ops ▸ Reports',         group: 'Dev Ops' },
  { pageKey: 'block-pre-flight', label: 'Dev Ops ▸ Block Pre-Flight', group: 'Dev Ops' },
  { pageKey: 'qa-management', label: 'Dev Ops ▸ QA Management', group: 'Dev Ops' },
  // View-only capability inside Reports. Colon namespacing keeps it out of
  // route/landing-page resolution while allowing admins to grant the tab
  // independently from the operational report desk.
  { pageKey: 'reports:admin-insights', label: 'Dev Ops ▸ Reports ▸ Admin Insights', group: 'Capabilities', special: true, permissionShape: 'read-only', description: 'View-only: this surface has no write actions of its own.' },

  // ─── Raw Tables ────────────────────────────────────────────────
  // Read-only PostgREST browser for a curated set of tables, used for
  // production data verification when the curated UI hides a field.
  // `raw-tables` is the page-entry gate; each `raw:<table>` sub-key
  // independently controls which tables show up in the dropdown.
  // Entry is granted by holding the parent OR any sub-key (mirrors the
  // administration / property-setup pattern). Read-only — only
  // can_read is meaningful.
  { pageKey: 'raw-tables',                label: 'Raw Tables',                        group: 'Raw Tables', special: true },
  { pageKey: 'raw:events',                label: 'Raw ▸ events',                      group: 'Raw Tables', special: true },
  { pageKey: 'raw:event_sessions',        label: 'Raw ▸ event_sessions',              group: 'Raw Tables', special: true },
  { pageKey: 'raw:event_space_bookings',  label: 'Raw ▸ event_space_bookings',        group: 'Raw Tables', special: true },
  { pageKey: 'raw:event_products',        label: 'Raw ▸ event_products',              group: 'Raw Tables', special: true },
  { pageKey: 'raw:event_product_consumptions', label: 'Raw ▸ event_product_consumptions', group: 'Raw Tables', special: true },
  { pageKey: 'raw:tasks',                 label: 'Raw ▸ tasks',                       group: 'Raw Tables', special: true },
  { pageKey: 'raw:pms_reservations',      label: 'Raw ▸ pms_reservations',            group: 'Raw Tables', special: true },
  { pageKey: 'raw:pms_blocks',            label: 'Raw ▸ pms_blocks',                  group: 'Raw Tables', special: true },
  { pageKey: 'raw:pms_room_types',        label: 'Raw ▸ pms_room_types',              group: 'Raw Tables', special: true },
  { pageKey: 'raw:spaces',                label: 'Raw ▸ spaces',                      group: 'Raw Tables', special: true },
  { pageKey: 'raw:products',              label: 'Raw ▸ products',                    group: 'Raw Tables', special: true },
  { pageKey: 'raw:rates',                 label: 'Raw ▸ rates',                       group: 'Raw Tables', special: true },
  { pageKey: 'raw:tax_rates',             label: 'Raw ▸ tax_rates',                   group: 'Raw Tables', special: true },
  { pageKey: 'raw:rules',                 label: 'Raw ▸ rules',                       group: 'Raw Tables', special: true },
  { pageKey: 'raw:users',                 label: 'Raw ▸ users',                       group: 'Raw Tables', special: true },
  { pageKey: 'raw:properties',            label: 'Raw ▸ properties',                  group: 'Raw Tables', special: true },
  { pageKey: 'raw:accounts',              label: 'Raw ▸ accounts',                    group: 'Raw Tables', special: true },
  { pageKey: 'raw:parties',               label: 'Raw ▸ parties',                     group: 'Raw Tables', special: true },

  // ─── AI ────────────────────────────────────────────────────────
  // Maya's three levels ride on the standard page triple, consumed by the
  // backend's capability resolver (services/maya/mayaCapabilities.ts):
  //   can_read   → may open Maya and let it READ data (GET)
  //   can_create → Maya may CREATE rows on this user's behalf (POST)
  //   can_edit   → Maya may MODIFY or DELETE rows (PATCH/DELETE)
  // The assistant's own `can_write` flag still has to be on as well — the two
  // are ANDed, so granting a role here cannot make a read-only assistant write.
  {
    pageKey: 'maya-assistant',
    label: 'Maya AI Assistant',
    group: 'AI',
    special: true,
    description: 'Read = open Maya and let it read data · Create = Maya may create rows for this user · Edit = Maya may change or delete rows. The assistant\'s own write flag is ANDed with this, so a read-only assistant stays read-only.',
  },
  {
    pageKey: 'maya-admin',
    label: 'Maya Admin (Settings)',
    group: 'AI',
    special: true,
    description: 'Configure the assistant itself — model, prompts, and which accounts it may act for.',
  },

  // ─── Tools ─────────────────────────────────────────────────────
  // `reports` (the page) and `azure-pbis` moved to Dev Ops where they
  // sit alongside their sibling tabs. `report` (singular) stays here
  // — it's the "submit a bug report" action shown in the user-menu
  // footer, unrelated to the Reports page.
  { pageKey: 'report', label: 'Report', group: 'Tools' },
  { pageKey: 'documentation', label: 'Documentation', group: 'Tools' },
  { pageKey: 'system-status', label: 'System Status', group: 'Tools' },
  // Public-facing roadmap surface — designed to show prospects where
  // EventHub is going. Read-only; no create / edit semantics.
  { pageKey: 'roadmap', label: 'Roadmap', group: 'Tools' },

  // ─── API Access (M2M + JWT) ────────────────────────────────────
  // Backend API surfaces that accept either an M2M env token (used by
  // the middleware / reporting integrations) OR a user JWT carrying the
  // matching capability. Adding a user to one of these gives them a
  // normal user account that can call the same endpoints the M2M
  // service accounts use — useful for support / QA / operators who
  // need to run manual sync jobs without borrowing a shared secret.
  // Shape is the standard read/create/edit triple:
  //   can_read   → GET endpoints
  //   can_create → POST endpoints
  //   can_edit   → PATCH/PUT/DELETE endpoints
  { pageKey: 'api-middleware', label: 'API ▸ Middleware (MICE · BS Upsert · Participants · Rates)', group: 'API Access', special: true },
  { pageKey: 'api-spaces',     label: 'API ▸ Spaces (Availabilities)',                              group: 'API Access', special: true },
  { pageKey: 'api-reports',    label: 'API ▸ Reports (Power BI · Charged Products)',                group: 'API Access', special: true },
  { pageKey: 'api-events',     label: 'API ▸ Events (General)',                                     group: 'API Access', special: true },

  // ─── Archive (legacy / lookup-only) ────────────────────────────
  { pageKey: 'event-sessions', label: 'Event Sessions', group: 'Archive' },
  { pageKey: 'attendees', label: 'Attendees', group: 'Archive' },
  { pageKey: 'spaces', label: 'Spaces', group: 'Archive' },
  { pageKey: 'menus', label: 'Menus', group: 'Archive' },
]

export interface RolePermission {
  id?: number
  role_name: string
  page_key: string
  can_read: boolean
  can_create: boolean
  can_edit: boolean
  /**
   * Optional because it arrives with V201: a frontend deployed before the
   * migration reads rows that simply do not carry the field, and the store
   * falls back to `can_edit` for those.
   */
  can_delete?: boolean
}

/**
 * Recognised event_stream values. Used by useStreamAccess() and any
 * surface that needs to enumerate streams (e.g. the create-event
 * picker). Kept aligned with the backend's streamConfig keys.
 */
export const EVENT_STREAMS = ['mice', 'internal', 'seminars', 'bs'] as const
export type EventStream = (typeof EVENT_STREAMS)[number]
